import { useCallback, useMemo } from 'react';
import postal from 'postal';
import Directions from '@alltrails/shared/icons/Directions';
import { COLOR_BRAND_PRIMARY } from '@alltrails/shared/denali/tokens';
import useUser from 'hooks/useUser';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import ActionBarItem from '../ActionBarItem';
import { onRoadblockedClick, buildSignupUrl } from '../../utils/roadblocks';
import { logTrailDetailsDirectionsClicked } from 'utils/trail_analytics_helpers';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { TrailId } from 'types/Trails';

const messagingChannel = postal.channel('explore_messagingChannel');

export type DirectionsButtonProps = {
  atRef: string;
  disabled: boolean;
  event: string;
  geoloc: { lat: number; lng: number };
  mobileBrowser: boolean;
  trailId: TrailId;
};

const DirectionsButton = ({
  event = '',
  mobileBrowser = false,
  disabled = false,
  atRef = '',
  geoloc = { lat: null, lng: null },
  trailId
}: DirectionsButtonProps) => {
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const baseDirectionsUrl = `https://www.google.com/maps/dir/Current+Location/${geoloc.lat},${geoloc.lng}`;

  const onClick = useCallback(() => {
    logTrailDetailsDirectionsClicked(trailId);

    if (!user) {
      window.open(buildSignupUrl(baseDirectionsUrl, atRef, languageRegionCode));
      return;
    }

    if (mobileBrowser) {
      onRoadblockedClick(messagingChannel, event, user, baseDirectionsUrl, {
        atRef,
        returnToUrl: baseDirectionsUrl,
        openInNewTab: true
      });
      return;
    }

    window.open(baseDirectionsUrl);
  }, [atRef, user, baseDirectionsUrl, mobileBrowser, trailId, languageRegionCode]);

  const actionBarProps = useMemo(() => {
    const props = { title: StringConstantsMixin.strings.TRACK_DETAIL.DIRECTIONS };

    if (disabled || !geoloc.lat || !geoloc.lng) {
      return Object.assign(props, { disabled });
    }

    return Object.assign(props, { onClick });
  }, [disabled, geoloc.lat, geoloc.lng, onClick]);

  return <ActionBarItem icon={<Directions color={COLOR_BRAND_PRIMARY} />} testId="directions-action" {...actionBarProps} />;
};

export default DirectionsButton;
